import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent } from "@/components/ui/tooltip";
import { DropdownOption } from "@/models/Dashboard";
import clsx from "clsx";
import { MinusIcon, PlusIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState, useMemo } from "react";

interface Props {
  title: string;
  options: DropdownOption[];
  selectedOptions: Map<number, number>;
  onQuantityChange: (id: number, newQuantity: number) => void;
  dropdownOpen?: boolean;
  setDropdownOpen?: (open: boolean) => void;
  hasBorder?: boolean;
}

export const SelectWithQuantities = ({
  title,
  options,
  selectedOptions,
  onQuantityChange,
  dropdownOpen: controlledOpen,
  setDropdownOpen: setControlledOpen,
  hasBorder,
}: Props) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const dropdownOpen =
    controlledOpen !== undefined ? controlledOpen : uncontrolledOpen;
  const setDropdownOpen = setControlledOpen || setUncontrolledOpen;
  const containerRef = useRef<HTMLDivElement>(null);

  // Build summary string for selected options
  const selectedSummary = Array.from(selectedOptions.entries())
    .filter(([_, qty]) => qty > 0)
    .map(([id, qty]) => {
      const opt = options.find((o) => o.id === id);
      return opt ? `${qty}x ${opt.value}` : null;
    })
    .filter(Boolean)
    .join(", ");

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [dropdownOpen]);

  // Fix: Always allow dropdown to open on click, even after selection
  // Remove onMouseDown={e => e.preventDefault()} (it prevents focus and click)
  // Instead, just use onClick to toggle dropdown

  return (
    <div className="inline-flex relative w-full" ref={containerRef}>
      <Tooltip>
        <button
          type="button"
          className={clsx(
            `flex items-center justify-between w-full px-3 py-1.5
            rounded-md
            dark:bg-zinc-900 bg-white dark:text-zinc-200 text-zinc-600
            text-sm font-normal shadow-sm transition
            hover:border-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500
            disabled:opacity-50 border`,
            hasBorder ? "dark:border-white/10" : "border-transparent"
          )}
          // Remove onMouseDown, just use onClick
          onClick={() => setDropdownOpen((v) => !v)}
        >
          <span
            className={
              (selectedSummary.length > 0 ? "" : "text-zinc-400 ") +
              "truncate block w-full text-left"
            }
            style={{ maxWidth: "100%" }}
            title={selectedSummary}
          >
            {selectedSummary.length > 0 ? selectedSummary : "Make selection"}
          </span>
          <svg
            className="ml-2 h-3.5 w-3.5 text-zinc-400 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path
              d="M6 9l6 6 6-6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <TooltipContent>
          <p>{selectedSummary}</p>
        </TooltipContent>
      </Tooltip>

      <AnimatePresence>
        {dropdownOpen && (
          <motion.div
            key="dropdown"
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{
              type: "spring",
              stiffness: 350,
              damping: 30,
              duration: 0.18,
            }}
            className={`
              absolute z-50 mt-2 min-w-64 rounded-md border dark:border-zinc-800 border-zinc-200
              dark:bg-zinc-900 bg-white shadow-xl py-1.5 top-8
            `}
          >
            <div className="px-3 py-0.5 text-xs dark:text-zinc-400 text-zinc-600 my-1">
              {title}
            </div>
            <div className="max-h-52 overflow-y-auto">
              {options.map((option) => {
                const currentQuantity = selectedOptions.get(option.id) || 0;
                return (
                  <div
                    key={option.id}
                    className={`
                      flex items-center px-2 py-1 mx-1.5 rounded
                      dark:hover:bg-zinc-800/50 hover:bg-zinc-100 transition cursor-pointer
                    `}
                  >
                    <Checkbox
                      checked={currentQuantity > 0}
                      onCheckedChange={(checked) => {
                        onQuantityChange(option.id, checked ? 1 : 0);
                      }}
                    />
                    <div className="flex-1 ml-2 dark:text-zinc-100 text-zinc-600 text-sm">
                      {option.value}
                    </div>
                    <div className="flex items-center">
                      <button
                        type="button"
                        onClick={() =>
                          onQuantityChange(option.id, currentQuantity - 1)
                        }
                        className={`
                          rounded p-0.5 dark:hover:bg-zinc-700 hover:bg-zinc-200 transition
                          disabled:opacity-40 cursor-pointer
                        `}
                        disabled={currentQuantity <= 0}
                      >
                        <MinusIcon
                          size={14}
                          className="text-zinc-600 dark:text-zinc-200"
                        />
                      </button>
                      <div className="mx-1 w-4 text-center tabular-nums dark:text-zinc-200 text-zinc-600 text-sm">
                        {currentQuantity}
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          onQuantityChange(option.id, currentQuantity + 1)
                        }
                        className={`
                          rounded p-0.5 dark:hover:bg-zinc-700 hover:bg-zinc-200 transition
                          cursor-pointer
                        `}
                      >
                        <PlusIcon
                          size={14}
                          className="text-zinc-600 dark:text-zinc-200"
                        />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
